'use client';

import * as React from 'react';

import { Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface TimePickerProps {
  defaultTime?: Date;
  onSelect?: (date: Date) => void;
  className?: string;
}

export function TimePicker({ defaultTime, onSelect, className }: TimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [internalDate, setInternalDate] = React.useState<Date>(() => defaultTime || new Date());

  // Get hours and minutes from the date
  const hours = internalDate.getHours();
  const minutes = internalDate.getMinutes();

  // Format for display
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  // Handle time change
  const handleTimeChange = (newHours: number, newMinutes: number) => {
    const newDate = new Date(internalDate);
    newDate.setHours(newHours);
    newDate.setMinutes(newMinutes);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    setInternalDate(newDate);
    onSelect?.(newDate);
  };

  // Generate options for hours and minutes
  const hoursOptions = Array.from({ length: 24 }, (_, i) => i);
  const minutesOptions = Array.from({ length: 12 }, (_, i) => i * 5);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="field" className={cn('w-full', className)}>
          <Clock className="text-border mr-2 h-4 w-4 dark:text-white" />
          {`${formattedHours}:${formattedMinutes}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="flex gap-2">
          <div className="flex flex-col">
            <span className="mb-2 text-sm font-medium">Hours</span>
            <Select
              value={hours.toString()}
              onValueChange={(value) => {
                handleTimeChange(Number.parseInt(value), minutes);
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder="Hour" />
              </SelectTrigger>
              <SelectContent>
                {hoursOptions.map((hour) => (
                  <SelectItem key={hour} value={hour.toString()}>
                    {hour.toString().padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col">
            <span className="mb-2 text-sm font-medium">Minutes</span>
            <Select
              value={Math.min(55, Math.round(minutes / 5) * 5).toString()}
              onValueChange={(value) => {
                handleTimeChange(hours, Number.parseInt(value));
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder="Minute" />
              </SelectTrigger>
              <SelectContent>
                {minutesOptions.map((minute) => (
                  <SelectItem key={minute} value={minute.toString()}>
                    {minute.toString().padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button size="sm" variant="secondary" onClick={() => setOpen(false)} className="self-end">
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
